import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Footer } from '../components/layout/Footer';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, user, loading } = useAuth();
  const navigate = useNavigate();
  
  // Función para redirigir según el rol
  const redirectByRole = (userRole: string) => {
    const roleRoutes: Record<string, string> = {
      management: '/management',
      admin: '/admin',
      client: '/management',
      establishment: '/display'
    };
    const targetPath = roleRoutes[userRole] || '/management';
    console.log('➡️ [Login] Redirigiendo a:', targetPath);
    navigate(targetPath, { replace: true });
  };

  // Redirigir si ya está autenticado
  useEffect(() => {
    console.log('🔄 [Login] useEffect de redirección ejecutado:', {
      isAuthenticated,
      hasUser: !!user,
      userRole: user?.role,
      loading,
      currentPath: window.location.pathname
    });

    if (isAuthenticated && user && !loading) {
      const roleRoutes: Record<string, string> = {
        management: '/management',
        admin: '/admin',
        client: '/management', // Client también va a /management
        establishment: '/display'
      };
      // Siempre redirigir a la página principal del rol, ignorar location.state.from
      // Esto previene redirecciones a rutas donde el usuario estaba antes de cerrar sesión
      const targetPath = roleRoutes[user.role] || '/management';
      
      console.log('🎯 [Login] Target path calculado:', targetPath);
      
      // Redirigir si no estamos ya en la ruta correcta
      // Permitir redirigir desde /login (es el caso normal después de login exitoso)
      if (window.location.pathname !== targetPath) {
        console.log('➡️ [Login] Redirigiendo a:', targetPath);
        navigate(targetPath, { replace: true });
      } else {
        console.log('⏸️ [Login] No se redirige porque ya estamos en:', targetPath);
      }
    } else {
      console.log('⏸️ [Login] No se cumple la condición de redirección:', {
        isAuthenticated,
        hasUser: !!user,
        loading
      });
    }
  }, [isAuthenticated, user, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, password);
      
      // Esperar un momento para que el estado se actualice
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Verificar autenticación y redirigir directamente
      if (isAuthenticated && user) {
        console.log('➡️ [Login] Redirigiendo desde handleSubmit');
        redirectByRole(user.role);
      }
    } catch (err: any) {
      setError(err.message || 'Error de autenticación');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900 to-slate-900 flex flex-col relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-rose-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-red-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Login Card Container */}
      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="bg-white/95 backdrop-blur-sm p-8 sm:p-10 rounded-2xl shadow-2xl w-full max-w-md border border-white/20">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl mb-4 shadow-lg">
            <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <rect x="3" y="4" width="18" height="14" rx="2" ry="2" />
              <line x1="7" y1="8" x2="17" y2="8" />
              <line x1="7" y1="12" x2="17" y2="12" />
              <line x1="7" y1="16" x2="13" y2="16" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent mb-2">
            HioMenu
          </h1>
          <p className="text-gray-600 text-sm font-medium">
            Sistema de gestión de menús digitales
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            type="text"
            label="Email o Usuario"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@hiomenu.test"
            required
            autoComplete="username"
            disabled={isLoading}
          />

          <Input
            type="password"
            label="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="current-password"
            disabled={isLoading}
          />

          {error && <Alert variant="error">{error}</Alert>}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            isLoading={isLoading}
          >
            Iniciar Sesión
          </Button>
        </form>
        </div>
      </div>

      {/* Add animations */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      {/* Footer */}
      <Footer />
    </div>
  );
};
